import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {
    CheckCircle2,
    CircleAlert,
    Clock3,
    LoaderCircle,
    MapPin,
    RotateCcw,
    TicketPercent
} from 'lucide-react';
import {
    getPromotionRedemptionByToken,
    redeemPromotionByToken,
    PromotionRedeemResult,
    PromotionTokenPreviewResponse,
    PromotionTokenPreviewResult
} from '../api';
import soltLogo from '../assets/logo.png';
import styles from './PromotionRedeem.module.css';

const cx = (...names: string[]) =>
    names
        .map((name) => styles[name])
        .filter(Boolean)
        .join(' ');

const formatDateTime = (value?: string) => {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

const getLocationLabel = (place?: PromotionTokenPreviewResponse['place']) => {
    if (!place) {
        return null;
    }

    const parts = [place.city, place.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
};

const getPromotionSummary = (promotion?: PromotionTokenPreviewResponse['promotion']) => {
    if (!promotion) {
        return null;
    }

    if (promotion.discountType === 'percentage' && promotion.discountValue !== undefined) {
        return `${promotion.discountValue}% off`;
    }

    if (promotion.discountType === 'fixed_amount' && promotion.discountValue !== undefined) {
        return `${promotion.discountValue} off`;
    }

    if (promotion.discountType === 'free_item') {
        return 'Free item';
    }

    return 'Partner promotion';
};

const PromotionRedeem: React.FC = () => {
    const {token} = useParams<{token: string}>();
    const [previewResult, setPreviewResult] = useState<PromotionTokenPreviewResult | null>(null);
    const [redeemResult, setRedeemResult] = useState<PromotionRedeemResult | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRedeeming, setIsRedeeming] = useState(false);

    useEffect(() => {
        let isActive = true;

        const fetchOffer = async () => {
            setIsLoading(true);
            setRedeemResult(null);

            const result = await getPromotionRedemptionByToken(token || '');
            if (!isActive) {
                return;
            }

            setPreviewResult(result);
            setIsLoading(false);
        };

        fetchOffer();

        return () => {
            isActive = false;
        };
    }, [token]);

    const handleRedeem = async () => {
        if (!token || isRedeeming) {
            return;
        }

        setIsRedeeming(true);
        const result = await redeemPromotionByToken(token);
        setRedeemResult(result);
        setIsRedeeming(false);
    };

    const handleRefresh = async () => {
        if (!token) {
            return;
        }

        setIsLoading(true);
        setRedeemResult(null);
        const result = await getPromotionRedemptionByToken(token);
        setPreviewResult(result);
        setIsLoading(false);
    };

    const activeResponse =
        redeemResult && 'data' in redeemResult ? redeemResult.data :
            previewResult && 'data' in previewResult ? previewResult.data :
                null;
    const activePromotion = activeResponse?.promotion || activeResponse?.offer || null;

    let statusTone: 'loading' | 'ready' | 'success' | 'warning' | 'error' = 'ready';
    let statusLabel = 'Ready to accept';
    let title = 'Accept this promotion';
    let description = 'Open the promotion details below and confirm the redemption with the restaurant staff.';
    let canRedeem = false;
    let showCenteredRefresh = false;

    if (isLoading) {
        statusTone = 'loading';
        statusLabel = 'Checking token';
        title = 'Loading promotion';
        description = 'We are validating this promotion token now.';
    } else if (!previewResult || previewResult.status === 'error') {
        statusTone = 'error';
        statusLabel = 'Server issue';
        title = 'We could not load this promotion';
        description = 'Our server may be having a moment. Please refresh and try again.';
        showCenteredRefresh = true;
    } else if (previewResult.status === 'not-found') {
        statusTone = 'error';
        statusLabel = 'Invalid QR code';
        title = 'Promotion not found';
        description = 'This QR code is not valid anymore, or the link was changed.';
    } else if (redeemResult?.status === 'redeemed') {
        statusTone = 'success';
        statusLabel = 'Accepted';
        title = 'Promotion accepted';
        description = 'This promotion has been accepted successfully.';
    } else if (redeemResult?.status === 'already-redeemed' || previewResult.status === 'redeemed') {
        statusTone = 'warning';
        statusLabel = 'Already accepted';
        title = 'Promotion already used';
        description = 'This QR code has already been accepted.';
    } else if (
        redeemResult?.status === 'expired' ||
        previewResult.status === 'expired' ||
        previewResult.status === 'inactive'
    ) {
        statusTone = 'warning';
        statusLabel = previewResult.status === 'inactive' ? 'Inactive promotion' : 'Expired QR code';
        title = previewResult.status === 'inactive' ? 'Promotion unavailable' : 'Promotion expired';
        description = previewResult.status === 'inactive'
            ? 'This promotion is no longer active.'
            : 'This QR code expired before it was accepted.';
    } else if (previewResult.status === 'valid') {
        statusTone = 'ready';
        statusLabel = 'Ready to accept';
        title = 'Accept this promotion';
        description = 'Confirm this only when the person is here and ready to use it.';
        canRedeem = true;
    }

    const locationLabel = getLocationLabel(activeResponse?.place);
    const offerSummary = getPromotionSummary(activePromotion || undefined);
    const redeemedAtLabel = formatDateTime(activeResponse?.redeemedAt);
    const expiresAtLabel = formatDateTime(activeResponse?.expiresAt);

    return (
        <div className={styles['offer-redeem-page']}>
            <div className={styles['offer-redeem-shell']}>
                <div className={styles['offer-redeem-brand-row']}>
                    <Link className={styles['offer-redeem-brand-link']} to="/">
                        <img src={soltLogo} alt="Solt logo" className={styles['offer-redeem-logo']}/>
                    </Link>
                    <span className={styles['offer-redeem-brand-pill']}>Promotions</span>
                </div>

                <div className={cx('offer-redeem-card', `offer-redeem-card--${statusTone}`)}>
                    <div className={styles['offer-redeem-status']}>
                        {statusTone === 'success' ? <CheckCircle2 size={18}/> : null}
                        {statusTone === 'warning' ? <Clock3 size={18}/> : null}
                        {statusTone === 'error' ? <CircleAlert size={18}/> : null}
                        {statusTone === 'ready' ? <TicketPercent size={18}/> : null}
                        {statusTone === 'loading' ? <LoaderCircle size={18} className={styles['offer-redeem-spin']}/> : null}
                        <span>{statusLabel}</span>
                    </div>

                    <h1 className={styles['offer-redeem-title']}>{title}</h1>
                    <p className={styles['offer-redeem-description']}>{description}</p>

                    {activeResponse ? (
                        <div className={styles['offer-redeem-details']}>
                            {activePromotion ? (
                                <div className={cx('offer-redeem-detail-card', 'offer-redeem-detail-card--primary')}>
                                    <div className={styles['offer-redeem-detail-header']}>
                                        <div className={styles['offer-redeem-detail-label']}>
                                            <TicketPercent size={16}/>
                                            <span>Promotion</span>
                                        </div>
                                        {offerSummary ? (
                                            <div className={styles['offer-redeem-detail-chip']}>{offerSummary}</div>
                                        ) : null}
                                    </div>
                                    <div className={styles['offer-redeem-detail-value']}>{activePromotion.title}</div>
                                    {activePromotion.description ? (
                                        <div className={styles['offer-redeem-detail-text']}>{activePromotion.description}</div>
                                    ) : null}

                                    {activeResponse.place?.name ? (
                                        <div className={styles['offer-redeem-place-extension']}>
                                            <div className={styles['offer-redeem-place-name-row']}>
                                                <MapPin size={16}/>
                                                <div className={styles['offer-redeem-place-name']}>{activeResponse.place.name}</div>
                                            </div>
                                            {locationLabel ? (
                                                <div className={styles['offer-redeem-place-location']}>
                                                    <span>{locationLabel}</span>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}

                            {!activePromotion && activeResponse.place?.name ? (
                                <div className={styles['offer-redeem-place-block']}>
                                    <div className={styles['offer-redeem-place-name-row']}>
                                        <MapPin size={16}/>
                                        <div className={styles['offer-redeem-place-name']}>{activeResponse.place.name}</div>
                                    </div>
                                    {locationLabel ? (
                                        <div className={styles['offer-redeem-place-location']}>
                                            <span>{locationLabel}</span>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}

                            {expiresAtLabel && canRedeem ? (
                                <div className={styles['offer-redeem-meta-row']}>
                                    <Clock3 size={14}/>
                                    <span>Valid until {expiresAtLabel}</span>
                                </div>
                            ) : null}

                            {redeemedAtLabel && !canRedeem ? (
                                <div className={styles['offer-redeem-meta-row']}>
                                    <CheckCircle2 size={14}/>
                                    <span>Accepted on {redeemedAtLabel}</span>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    <div
                        className={cx(
                            'offer-redeem-actions',
                            showCenteredRefresh ? 'offer-redeem-actions--centered' : ''
                        )}
                    >
                        {canRedeem ? (
                            <button
                                className={styles['offer-redeem-primary-button']}
                                disabled={isRedeeming}
                                onClick={handleRedeem}
                                type="button"
                            >
                                {isRedeeming ? (
                                    <>
                                        <LoaderCircle size={16} className={styles['offer-redeem-spin']}/>
                                        Accepting...
                                    </>
                                ) : (
                                    'Accept promotion'
                                )}
                            </button>
                        ) : null}

                        {(statusTone === 'error' || statusTone === 'warning') ? (
                            <button
                                className={styles['offer-redeem-secondary-button']}
                                onClick={handleRefresh}
                                type="button"
                            >
                                <RotateCcw size={15}/>
                                Refresh
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromotionRedeem;
